// src/pages/container_rental/components/timeline/TimelineRow.tsx
//
// 한 행 = [좌측 라벨(sticky left)] + [트랙].
// 라벨은 가로 스크롤에 붙어 있어야 하므로 반드시 불투명 배경을 가진다.
//
// 상태별 흐리기(draft 0.9 / closed 0.85 / cancelled 0.65)를 행 컨테이너에 걸면
// 안 된다 — opacity < 1 은 스태킹 컨텍스트를 만들어 sticky 라벨의 zIndex:3 이
// 행 안에 갇히고, 그러면 zIndex:1 인 오늘선이 그 행의 라벨 위로 그어진다
// (오늘선보다 오른쪽으로 스크롤했을 때 active 행만 정상으로 보이는 증상).
// 그래서 흐리기는 트랙과 "라벨의 내용물"에만 건다. 라벨 배경은 언제나 불투명.

import { Box } from "@mui/material";
import type { Dayjs } from "dayjs";

import { designTokens } from "../../../../theme.ts";
import type { TimelineRowDto } from "../../../../contracts/container_rental/timeline";
import StatusChip from "../StatusChip.tsx";
import { rentalVisual } from "../statusVisuals.ts";
import RentalBar from "./RentalBar.tsx";

const T = designTokens;

/** 시스템 뉴트럴 — 타임라인 격자 전용(구 잉크 먹선 대체) */
const LINE = {
  /** 행 구분선 */
  row: "#EDEFF1",
  /** 라벨 컬럼 경계 */
  border: "#E4E7EA",
  /** 행 호버 — 앱 전역 중립 호버와 동일 */
  hover: "#F2F4F5",
  sub: "#6B7178",
} as const;

interface TimelineRowProps {
  row: TimelineRowDto;
  /** 도메인 시작(= 트랙 x 0) */
  origin: Dayjs;
  /** 도메인 끝(포함) — 이 밖으로 나간 바는 잘라 그린다 */
  domainEnd: Dayjs;
  ppd: number;
  today: Dayjs;
  chartWidth: number;
  labelWidth: number;
  rowHeight: number;
  /** 가로 컬링 창(px, 트랙 좌표). null 이면 컬링하지 않는다. */
  viewFrom?: number | null;
  viewTo?: number | null;
  onOpenDetail: (id: string) => void;
}

export default function TimelineRow({
  row,
  origin,
  domainEnd,
  ppd,
  today,
  chartWidth,
  labelWidth,
  rowHeight,
  viewFrom = null,
  viewTo = null,
  onOpenDetail,
}: TimelineRowProps) {
  const visual = rentalVisual(row.status);
  const overdue = Number.isFinite(row.overdueCount) ? row.overdueCount : 0;

  const spec = [
    row.siteName || "",
    row.containerModel
      ? `${row.containerModel}×${row.containerCount ?? 0}`
      : "",
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "stretch",
        height: rowHeight,
        borderBottom: `1px solid ${LINE.row}`,
        "&:hover .crt-row-label": { bgcolor: LINE.hover },
      }}
    >
      {/* 라벨 (sticky left) */}
      <Box
        className="crt-row-label"
        role="button"
        tabIndex={0}
        onClick={() => onOpenDetail(row.id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpenDetail(row.id);
          }
        }}
        sx={{
          position: "sticky",
          left: 0,
          zIndex: 3,
          flex: "0 0 auto",
          width: labelWidth,
          bgcolor: T.paper,
          borderRight: `1px solid ${LINE.border}`,
          px: 1.25,
          py: 0.75,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: 0.35,
          cursor: "pointer",
          minWidth: 0,
          transition: "background-color 120ms ease",
          // 배경은 불투명하게 두고 내용물만 흐린다(가로 스크롤 시 바가 비쳐
          // 보이면 안 되고, 라벨 자체가 스태킹 컨텍스트를 만들어도 무방하다).
          "& > *": { opacity: visual.rowOpacity },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              flexShrink: 0,
              bgcolor: visual.accent,
            }}
          />
          <Box
            sx={{
              fontFamily: T.fonts.sans,
              fontWeight: 700,
              fontSize: 13,
              color: T.ink,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              minWidth: 0,
            }}
          >
            {row.clientName || "—"}
          </Box>
        </Box>

        <Box
          sx={{
            fontFamily: T.fonts.mono,
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: "0.04em",
            color: LINE.sub,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {spec || "—"}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
          <StatusChip status={row.status} sx={{ height: 18 }} />
          {overdue > 0 && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.35,
                height: 18,
                px: 0.6,
                borderRadius: "6px",
                bgcolor: "#FBEDED",
                color: T.stop,
                border: "1px solid #F0D6D6",
                fontFamily: T.fonts.sans,
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: 0,
                lineHeight: 1,
                whiteSpace: "nowrap",
              }}
            >
              연체
              <Box component="span" sx={{ fontVariantNumeric: "tabular-nums" }}>
                {overdue}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* 트랙 — 상태별 흐리기는 여기에만 건다 */}
      <Box
        sx={{
          flex: "0 0 auto",
          width: chartWidth,
          position: "relative",
          opacity: visual.rowOpacity,
        }}
      >
        <RentalBar
          row={row}
          origin={origin}
          domainEnd={domainEnd}
          ppd={ppd}
          today={today}
          width={chartWidth}
          height={rowHeight}
          viewFrom={viewFrom}
          viewTo={viewTo}
          onOpenDetail={onOpenDetail}
        />
      </Box>
    </Box>
  );
}
